import styled from "styled-components";
import { COLORS } from "styles/colors";
import { FLEX } from "styles/flex";
import { calcRem } from "styles/theme";

/** react-to-print iframe 인쇄 시 배경색(썸네일 검정·헤더 등)이 빠지지 않도록 */
export const INSTALLATION_PRINT_PAGE_STYLE = `
  @page {
    size: auto;
    margin: 12mm;
  }
  @media print {
    html, body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    svg {
      color: inherit !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    img {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

export const Container = styled.div`
  width: 100%;
  background-color: white;

  /*
   * display:none이면 react-to-print가 iframe으로 복제할 때
   * 자식(16:9 padding 썸네일) 높이·배경이 비어 검은 배경이 인쇄되지 않을 수 있음.
   * 화면에서는 뷰포트 밖으로만 보내고, @media print 에서는 일반 흐름으로 복구.
   */
  @media screen {
    position: fixed;
    left: -10000px;
    top: 0;
    width: min(1024px, 100vw);
    max-width: 1024px;
    pointer-events: none;
    z-index: -1;
    overflow: visible;
  }

  @media print {
    position: static !important;
    left: auto !important;
    top: auto !important;
    width: 100% !important;
    max-width: none !important;
    z-index: auto !important;
    pointer-events: auto !important;
    .no-print {
      display: none !important;
    }
  }
  ${INSTALLATION_PRINT_PAGE_STYLE}

  .left-aside {
    width: 100%;
    min-width: 0;
    display: grid;
    /* minmax(0,1fr): 그리드 열이 콘텐츠 최소너비 때문에 페이지 밖으로 밀려 오른쪽 썸네일만 잘리는 현상 방지 */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  .right-wrapper {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    min-width: 0;

    padding-bottom: 10px;
    margin-bottom: 20px;
    border-bottom: 1px solid #828b9b;
  }

  .right-wrapper li {
    ${FLEX.FlexStartStart};
    padding: 8px 0;
    flex: 1;
    min-width: 0;

    .content {
      flex: 4;
      max-width: 100%;
      white-space: pre-wrap;
    }
    * {
      color: ${COLORS.BLACK};
    }

    gap: 6px;
    div {
      font-size: ${calcRem(14)};
    }
  }
`;

export const Header = styled.ul`
  ${FLEX.FlexCenterCenter};
  background-color: ${COLORS.PRIMARY1};
  background-image: linear-gradient(
    to bottom,
    ${COLORS.PRIMARY1},
    ${COLORS.PRIMARY1}
  ) !important;
  padding: 30px;
  color: ${COLORS.WHITE};
  font-weight: 700;
  font-size: ${calcRem(24)};

  ${INSTALLATION_PRINT_PAGE_STYLE}

  > span {
    color: ${COLORS.COMPANY};
    font-weight: 800;
    padding-left: 10px;
  }
`;

export const Inner = styled.div`
  padding: 30px;
  min-width: 0;
  box-sizing: border-box;
`;

export const PreviewTitle = styled.div`
  font-size: ${calcRem(20)};
  font-weight: 500;
`;

export const CameraItem = styled.div<{ isBreak: boolean }>`
  /* overflow: hidden; */
  border-radius: 0 0 4px 4px;
  background-color: #dddddd;
  background-image: linear-gradient(to bottom, #dddddd, #dddddd) !important;
  margin-top: 1rem;
  width: 100%;
  min-width: 0;
  max-width: 100%;

  ${INSTALLATION_PRINT_PAGE_STYLE}

  ${({ isBreak }) =>
    isBreak &&
    `
    @media all {
      display: none;
    }
      
    @media print {
      display: block;
      page-break-after: always;
    }
  `}
`;

export const CameraContent = styled.ul`
  ${FLEX.FlexCenterCenter};
  flex-direction: column;
  padding: 10px 0;
  flex: 1;
`;

export const CameraContentRow = styled.li`
  ${FLEX.FlexStartCenter};
  flex: 1;
  width: 90%;

  * {
    color: ${COLORS.BLACK};
  }

  > div {
    flex: 3;
  }
  .title {
    flex: 1;
  }

  &:nth-child(2) {
    margin: 10px 0;
  }
`;

export const BottomInfo = styled.div`
  * {
    color: ${COLORS.BLACK};
    font-size: ${calcRem(14)};
  }
  li {
    flex: 1;
    ${FLEX.FlexStartStart};
    gap: 20px;
    width: 100%;
    border-bottom: 1px solid ${COLORS.BLACK};
    padding: 6px;
    padding-top: 14px;

    &:last-child {
      border: none;
    }
  }
  .title {
    flex: 1;
    font-weight: 500;
  }

  .content {
    flex: 4;
    max-width: 100%;
    white-space: pre-wrap;
  }
`;

export const BottomWrapper = styled.div`
  ${FLEX.FlexBetweenStart};
  flex: 1;
  width: 100%;
  gap: 50px;
  border-top: 1px solid ${COLORS.BLACK};
  margin-top: 50px;
  padding: 30px 0 60px;

  > ul {
    width: 100%;
    flex: 1;
    ${FLEX.FlexStartStart};
    flex-direction: column;
  }
`;

export const Footer = styled.footer`
  width: 100%;
  > ul {
    width: 100%;
    ${FLEX.FlexStartCenter};
    gap: 10px;

    > li {
      border: none;

      &:last-child {
        flex: 2;
      }
    }
  }
`;
